import React, { useState } from 'react';
import { isToday } from 'date-fns';
import { Button, Group, Modal, ModalCloseReason, useToast } from '@economic/gaudi';
import { useCurrentAgreement } from '@economic/react-app';
import { t, tBB, useLocalization } from '../../l10n/l10n';
import useCachedQuery from '../../../hooks/useCachedQuery';
import { AddonUser, SubscriptionRetentionOptionEnum, SubscriptionRetentionResponse } from '../../../../../types/internal-api';
import internalApiClient from '../../../util/api/internalApiClient';

import manPointingToLaptop from './man-pointing-to-a-laptop.svg';

interface LicenseAccess {
    licenseModuleId: number;
    agreementId?: number;
    id?: number;
    unsubscriptionDate?: Date;
    subscriptionDate: Date;
    discount: number;
    shouldBeCredited: boolean;
    isPartOfPlan: boolean;
    discountExpiryDate?: Date;
    hasValue: boolean;
}

export default function SmartInboxSubscriptionRetentionDialog() {
    const SMART_INBOX_MODULE = 73;
    const CAMPAIGN_ID = 'SmartInboxTrial2021Autumn';
    const loadingTexts = useLocalization(textIds);
    const agreement = useCurrentAgreement();
    const [show, setShow] = useState(true);
    const toast = useToast();
    const smartInboxModuleQuery = useCachedQuery<LicenseAccess>(`/modules/${SMART_INBOX_MODULE}/access`);
    const addonUserQuery = useCachedQuery<AddonUser>('/modules/user/self');
    const smartInboxRetentionResponseQuery = useCachedQuery<SubscriptionRetentionResponse>(
        `/subscription-retention/${CAMPAIGN_ID}`
    );

    if (
        loadingTexts ||
        smartInboxModuleQuery.loading ||
        smartInboxRetentionResponseQuery.loading ||
        addonUserQuery.loading ||
        !show
    ) {
        return null;
    }

    const canToggleAddons = addonUserQuery.data?.permissions & 2; // see EconomicEnum.AddonUserPermission.AccountAdmin in e-conomic
    const hasFeatureFlag = agreement.features.smartInboxSubscriptionRetentionDialog;
    const userOption = smartInboxRetentionResponseQuery.data?.response;
    const hasOptedInOrOut =
        userOption === SubscriptionRetentionOptionEnum.OptIn || userOption === SubscriptionRetentionOptionEnum.OptOut;
    const hasSmartInboxAndDiscount =
        smartInboxModuleQuery.data?.discount === 100 &&
        !smartInboxModuleQuery.data?.unsubscriptionDate &&
        !!smartInboxModuleQuery.data?.discountExpiryDate;
    const hasSeenDialogToday = isToday(smartInboxRetentionResponseQuery.data?.timestamp);

    if (!canToggleAddons || !hasFeatureFlag || !hasSmartInboxAndDiscount || hasOptedInOrOut || hasSeenDialogToday) {
        return null;
    }

    function handlePopupClose(reason: ModalCloseReason) {
        if (reason === 'close') {
            saveOption(SubscriptionRetentionOptionEnum.Undecided);
        }
    }

    function handleSmartScanOptIn() {
        saveOption(SubscriptionRetentionOptionEnum.OptIn);
        setShow(false);
    }

    function handleSmartScanOptOut() {
        saveOption(SubscriptionRetentionOptionEnum.OptOut);
        setShow(false);
    }

    function saveOption(option: SubscriptionRetentionOptionEnum) {
        try {
            internalApiClient.post(`/subscription-retention/${CAMPAIGN_ID}`, { response: option });
        } catch {
            toast.error(t`TOAST_CAMPAIGNEND_RESPOND_ERROR`);
        }
    }

    return (
        <Modal
            size="xs"
            visible={show}
            hideOnClickOutside={false}
            hideOnEsc={false}
            aria-label={t('SMARTINBOXRETENTION_DIALOG_TITLE')}
            extra={t('SMARTINBOXRETENTION_DIALOG_EXTRA')}
            onHide={handlePopupClose}>
            <Modal.Header>{t('SMARTINBOXRETENTION_DIALOG_TITLE')}</Modal.Header>
            <Modal.Content>
                <div className="flex p-8 justify-center">
                    <img src={manPointingToLaptop} />
                </div>
                <div className="pb-3">{tBB('SMARTINBOXRETENTION_DIALOG_MAIN_TEXT')}</div>
                <div className="text-xs">{t('SMARTINBOXRETENTION_DIALOG_SECONDARY_TEXT')}</div>
            </Modal.Content>
            <Modal.Footer>
                <Group>
                    <Button onClick={handleSmartScanOptOut}>{t('SMARTINBOXRETENTION_DIALOG_BUTTON_OPTOUT')}</Button>
                    <Button appearance="primary" onClick={handleSmartScanOptIn}>
                        {t('SMARTINBOXRETENTION_DIALOG_BUTTON_OPTIN')}
                    </Button>
                </Group>
            </Modal.Footer>
        </Modal>
    );
}

const textIds = [
    'SMARTINBOXRETENTION_DIALOG_TITLE',
    'SMARTINBOXRETENTION_DIALOG_EXTRA',
    'SMARTINBOXRETENTION_DIALOG_MAIN_TEXT',
    'SMARTINBOXRETENTION_DIALOG_SECONDARY_TEXT',
    'SMARTINBOXRETENTION_DIALOG_BUTTON_OPTIN',
    'SMARTINBOXRETENTION_DIALOG_BUTTON_OPTOUT',
    'TOAST_CAMPAIGNEND_RESPOND_ERROR',
];
